import { useEffect, useState, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Plus, Pencil, Trash2, Database, Search, X, ChevronLeft, ChevronRight, EllipsisVertical, Copy, Maximize2, Minimize2 } from 'lucide-react'
import { dataSourceApi } from '@/api/services'
import { useToast } from '@/context/ToastContext'
import { Button, buttonVariants } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Badge, Spinner, EmptyState, Label } from '@/components/ui/misc'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'
import { DataSourceParameterToggles } from '@/components/DataSourceParameterToggles'
import { SnippetPicker } from '@/components/SnippetPicker'
import { shouldShowParameterPlaceholderHint } from '@/lib/injectedParameters'
import { mergeInputParameterFlags } from '@/lib/sqlPlaceholders'
import { defaultCloneKey } from '@/lib/cloneDefaults'
import { toastApiError } from '@/lib/apiError'
import type { DataSource } from '@/types'

type FormState = Omit<DataSource, 'id' | 'createdAt' | 'updatedAt' | 'version'>

const EMPTY_FORM: FormState = {
  key: '',
  description: '',
  sqlQuery: '',
  isCollection: true,
  inputParameters: [],
}
const PAGE_SIZE = 10

export default function DataSourcesPage() {
  const { toast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()

  const [sources, setSources]       = useState<DataSource[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage]             = useState(1)
  const [search, setSearch]         = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [loading, setLoading]       = useState(true)
  const [editing, setEditing]       = useState<DataSource | null>(null)
  const [creating, setCreating]     = useState(false)
  const [form, setForm]             = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving]         = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<DataSource | null>(null)
  const [deleting, setDeleting]     = useState(false)
  const [cloneModalOpen, setCloneModalOpen] = useState(false)
  const [cloneSource, setCloneSource] = useState<DataSource | null>(null)
  const [cloneKey, setCloneKey] = useState('')
  const [cloning, setCloning] = useState(false)
  const [formExpanded, setFormExpanded] = useState(false)

  const searchTimer = useRef<ReturnType<typeof setTimeout>>()
  const searchRef   = useRef<HTMLInputElement>(null)
  const sqlRef      = useRef<HTMLTextAreaElement>(null)
  const deepLinkKeyRef = useRef<string | null>(null)
  const deepLinkOpening = useRef(false)

  useEffect(() => {
    const key = searchParams.get('key')
    if (!key) return
    deepLinkKeyRef.current = key
    setSearch(key)
    setSearchParams({}, { replace: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once for initial ?key= deep link
  }, [])

  // Debounce search
  useEffect(() => {
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 350)
    return () => clearTimeout(searchTimer.current)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await dataSourceApi.getAll({
        filter: debouncedSearch || undefined,
        pageNumber:page,
        pageSize: PAGE_SIZE,
      })
      setSources(res.items)
      setTotalCount(res.totalCount)
      setTotalPages(res.totalPages)
    } catch (e: unknown) {
      toastApiError(toast, e, 'Failed to load data sources')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page, toast])

  useEffect(() => { load() }, [load])

  function openCreate() { setForm(EMPTY_FORM); setEditing(null); setFormExpanded(false); setCreating(true) }
  function openEdit(ds: DataSource) {
    setForm({
      key: ds.key,
      description: ds.description,
      sqlQuery: ds.sqlQuery,
      isCollection: ds.isCollection,
      inputParameters: mergeInputParameterFlags(ds.sqlQuery, ds.inputParameters ?? []),
    })
    setEditing(ds)
    setFormExpanded(false)
    setCreating(true)
  }
  function closeForm() { setCreating(false); setEditing(null); setFormExpanded(false) }

  useEffect(() => {
    const key = deepLinkKeyRef.current
    if (!key || loading || deepLinkOpening.current) return

    deepLinkOpening.current = true

    const tryOpen = async () => {
      try {
        const match = sources.find(s => s.key === key)
        if (match) {
          openEdit(match)
          return
        }
        const res = await dataSourceApi.getAll({ filter: key, pageSize: 100 })
        const found = res.items.find(s => s.key === key)
        if (found) {
          openEdit(found)
        } else {
          toast(`Data source not found: ${key}`, 'error')
        }
      } catch (e: unknown) {
        toastApiError(toast, e, `Data source not found: ${key}`)
      } finally {
        deepLinkKeyRef.current = null
        deepLinkOpening.current = false
      }
    }

    void tryOpen()
  }, [loading, sources, toast])

  async function handleSave() {
    if (!form.key || !form.sqlQuery) return toast('Key and SQL are required', 'error')
    setSaving(true)
    try {
      const payload = {
        ...form,
        inputParameters: mergeInputParameterFlags(form.sqlQuery, form.inputParameters ?? []),
      }
      if (editing) {
        await dataSourceApi.update(editing.id, payload)
        toast(`"${form.key}" updated`)
      } else {
        await dataSourceApi.create(payload)
        toast(`"${form.key}" created`)
      }
      closeForm()
      load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Save failed')
    }
    finally { setSaving(false) }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await dataSourceApi.delete(deleteTarget.id)
      toast(`"${deleteTarget.key}" deleted`)
      setDeleteTarget(null)
      load()
    }
    catch (e: unknown) {
      toastApiError(toast, e, 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  function openCloneModal(ds: DataSource) {
    setCloneSource(ds)
    setCloneKey(defaultCloneKey(ds.key))
    setCloneModalOpen(true)
  }

  function closeCloneModal() {
    setCloneModalOpen(false)
    setCloneSource(null)
    setCloneKey('')
  }

  async function confirmClone() {
    if (!cloneSource) return
    if (!cloneKey.trim()) {
      toast('Key is required', 'error')
      return
    }

    setCloning(true)
    try {
      const result = await dataSourceApi.clone(cloneSource.id, {
        key: cloneKey.trim().toLowerCase().replace(/\s+/g, '_'),
      })
      toast(`Cloned as "${result.key}"`)
      closeCloneModal()
      await load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Clone failed')
    } finally {
      setCloning(false)
    }
  }

  function clearSearch() { setSearch(''); searchRef.current?.focus() }

  function insertSqlSnippet(content: string) {
    const el = sqlRef.current
    if (!el) {
      setForm(p => ({ ...p, sqlQuery: p.sqlQuery ? `${p.sqlQuery}\n${content}` : content }))
      return
    }
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? start
    const next = el.value.slice(0, start) + content + el.value.slice(end)
    setForm(p => ({
      ...p,
      sqlQuery: next,
      inputParameters: mergeInputParameterFlags(next, p.inputParameters ?? []),
    }))
    requestAnimationFrame(() => {
      el.focus()
      const pos = start + content.length
      el.setSelectionRange(pos, pos)
    })
  }

  const from = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const to   = Math.min(page * PAGE_SIZE, totalCount)

  return (
    <div className="flex h-full overflow-hidden">
      {/* List panel */}
      {!(creating && formExpanded) && (
      <div className="flex flex-1 flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-4 border-b border-border px-6 py-4 bg-surface flex-shrink-0">
          <div className="min-w-0">
            <h1 className="text-sm font-black tracking-wider text-ink">DATA SOURCES</h1>
            <p className="text-xs text-muted font-mono mt-0.5">
              {loading ? 'Loading…' : `${totalCount} SQL source${totalCount !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-sm relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted pointer-events-none" />
            <input
              ref={searchRef}
              className="w-full rounded-md border border-border2 bg-bg pl-8 pr-8 py-1.5 text-xs font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none transition-colors"
              placeholder="Search by key or description…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink transition-colors">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="flex-1" />
          <Button onClick={openCreate}><Plus className="h-3.5 w-3.5" /> New Source</Button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-20"><Spinner className="h-6 w-6" /></div>
          ) : sources.length === 0 ? (
            <EmptyState message={debouncedSearch ? `No sources match "${debouncedSearch}"` : 'No data sources yet.'} />
          ) : (
            <div className="grid gap-3">
              {sources.map(ds => (
                <div
                  key={ds.id}
                  className="flex items-center gap-4 rounded-lg border border-border bg-surface px-5 py-4 hover:border-border2 transition-colors cursor-pointer"
                  onClick={() => openEdit(ds)}
                >
                  <Database className="h-4 w-4 text-accent2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-green font-medium">{ds.key}</span>
                      <Badge variant={ds.isCollection ? 'accent' : 'green'}>
                        {ds.isCollection ? 'array' : 'object'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted mt-0.5 truncate">{ds.description || 'No description'}</p>
                  </div>
                  {ds.updatedAt && (
                    <p className="text-[10px] text-muted font-mono hidden sm:block flex-shrink-0">
                      {new Date(ds.updatedAt).toLocaleDateString()}
                    </p>
                  )}
                  <div className="flex shrink-0 items-center" onClick={e => e.stopPropagation()}>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          className={cn(
                            buttonVariants({ variant: 'ghost', size: 'icon' }),
                            'text-muted hover:text-ink'
                          )}
                          aria-label={`Actions for ${ds.key}`}
                          title="Actions"
                        >
                          <EllipsisVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          align="end"
                          sideOffset={6}
                          className="z-50 min-w-[12.5rem] rounded-lg border border-border bg-surface py-1 shadow-xl"
                        >
                          <DropdownMenu.Item
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[highlighted]:bg-bg"
                            onSelect={() => openEdit(ds)}
                          >
                            <Pencil className="h-3.5 w-3.5 shrink-0 text-muted" />
                            Edit
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                            disabled={cloning}
                            onSelect={() => openCloneModal(ds)}
                          >
                            <Copy className="h-3.5 w-3.5 shrink-0 text-muted" />
                            Clone…
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-red outline-none data-[highlighted]:bg-red/10"
                            onSelect={() => setDeleteTarget(ds)}
                          >
                            <Trash2 className="h-3.5 w-3.5 shrink-0" />
                            Delete
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pager */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border bg-surface px-6 py-3 flex-shrink-0">
            <p className="text-[11px] text-muted font-mono">
              Showing <span className="text-ink">{from}–{to}</span> of <span className="text-ink">{totalCount}</span>
            </p>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce<(number | '...')[]>((acc, p, i, arr) => {
                  if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('...')
                  acc.push(p)
                  return acc
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`e${i}`} className="w-8 text-center text-xs text-muted">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className={cn(
                        'h-8 min-w-8 px-2 rounded-md text-xs font-mono transition-colors',
                        page === p ? 'bg-accent text-white font-bold' : 'text-muted hover:text-ink hover:bg-border'
                      )}
                    >{p}</button>
                  )
                )}
              <Button variant="ghost" size="icon" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Side form */}
      {creating && (
        <div className={cn(
          'border-l border-border bg-surface flex flex-col flex-shrink-0',
          formExpanded ? 'min-w-0 flex-1' : 'w-96'
        )}>
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-xs font-black tracking-wider text-ink">
              {editing ? 'EDIT SOURCE' : 'NEW SOURCE'}
            </h2>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setFormExpanded(p => !p)}
                className="px-2 text-muted hover:text-ink transition-colors"
                title={formExpanded ? 'Collapse' : 'Expand'}
              >
                {formExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>
              <Button variant="ghost" size="sm" onClick={closeForm}>Cancel</Button>
            </div>
          </div>
          <div className={cn(
            'flex-1 p-5',
            formExpanded ? 'flex flex-col overflow-hidden gap-4' : 'overflow-y-auto space-y-4'
          )}>
            <div className="flex-shrink-0">
              <Label>Key</Label>
              <Input placeholder="residents" value={form.key} onChange={e => setForm(p => ({ ...p, key: e.target.value }))} />
              <p className="mt-1 text-[10px] text-muted font-mono">Used in Liquid as {'{{ collection.key }}'}</p>
            </div>
            <div className="flex-shrink-0">
              <Label>Description</Label>
              <Input placeholder="Active residents list" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className={cn(formExpanded && 'flex flex-col flex-1 min-h-0')}>
              <Label>SQL Query</Label>
              <div className="mb-2 flex-shrink-0">
                <SnippetPicker type="DataSource" compact onInsert={insertSqlSnippet} />
              </div>
              <textarea
                ref={sqlRef}
                className={cn(
                  'w-full rounded-md border border-border2 bg-bg px-3 py-2 text-xs font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none resize-none transition-colors',
                  formExpanded ? 'flex-1 min-h-0' : 'h-48'
                )}
                placeholder={'SELECT Name, Gender\nFROM residents\nWHERE IsActive = true'}
                value={form.sqlQuery}
                onChange={e => {
                  const sql = e.target.value
                  setForm(p => ({
                    ...p,
                    sqlQuery: sql,
                    inputParameters: mergeInputParameterFlags(sql, p.inputParameters ?? []),
                  }))
                }}
              />
            </div>
            <div className={cn(formExpanded && 'flex-shrink-0 overflow-y-auto max-h-48')}>
              <Label>SQL parameters</Label>
              <div className="mt-2 rounded-md border border-border2 bg-bg px-3 py-3">
                <DataSourceParameterToggles
                  params={form.inputParameters ?? []}
                  onChange={next => setForm(p => ({ ...p, inputParameters: next }))}
                />
                {shouldShowParameterPlaceholderHint(form.inputParameters ?? []) ? (
                  <p className="text-[10px] text-muted font-mono">
                    Add placeholders like {'{{DateFrom}}'} or {'{{MyParam}}'} in the SQL above.
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-3">
              <Label className="mb-0" id="ds-is-collection-label">Returns array</Label>
              <button
                type="button"
                role="switch"
                aria-checked={form.isCollection}
                aria-labelledby="ds-is-collection-label"
                onClick={() => setForm(p => ({ ...p, isCollection: !p.isCollection }))}
                className={cn(
                  'relative h-5 w-10 flex-shrink-0 rounded-full transition-colors',
                  form.isCollection ? 'bg-accent' : 'bg-border2'
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                    form.isCollection ? 'translate-x-5' : 'translate-x-0'
                  )}
                />
              </button>
              <span className="text-[10px] text-muted font-mono">{form.isCollection ? 'array' : 'single object'}</span>
            </div>
          </div>
          <div className="border-t border-border p-5">
            <Button className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Update Source' : 'Create Source'}
            </Button>
          </div>
        </div>
      )}
      {cloneModalOpen && cloneSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <h2 className="text-xs font-black tracking-wider text-ink">CLONE DATA SOURCE</h2>
                <p className="mt-0.5 text-[10px] font-mono text-muted">
                  From <span className="text-green">{cloneSource.key}</span>
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeCloneModal} disabled={cloning}>
                Cancel
              </Button>
            </div>

            <div className="space-y-2 p-5">
              <Label>New key</Label>
              <Input
                value={cloneKey}
                onChange={e => setCloneKey(e.target.value)}
                disabled={cloning}
                className="font-mono"
              />
              <p className="text-[10px] text-muted font-mono">Used in Liquid as {'{{ collection.key }}'}</p>
            </div>

            <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
              <Button
                variant="green"
                onClick={() => {
                  void confirmClone()
                }}
                disabled={cloning}
              >
                {cloning ? 'Cloning…' : 'Clone'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="DELETE DATA SOURCE"
        message={deleteTarget ? `Delete "${deleteTarget.key}"? This action cannot be undone.` : ''}
        loading={deleting}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null)
        }}
        onConfirm={confirmDelete}
      />
    </div>
  )
}